<template>
  <div
    v-click-outside="closePopup"
    class="us-state-map"
  >
    <svg
      viewBox="0 0 960 620"
      xmlns="http://www.w3.org/2000/svg"
      @mouseleave="onSvgMouseLeave"
    >
      <!-- Main map states -->
      <g id="main-map">
        <path
          v-for="state in mainStates"
          :key="state.code"
          :d="state.path"
          :fill="getStateColor(state.code)"
          :stroke="getStateStroke(state.code)"
          stroke-width="1"
          class="state-path"
          @click.stop="handleStateClick(state.code)"
          @mouseenter="handleStateHover(state.code)"
        />
        <!-- State labels -->
        <text
          v-for="state in mainStates"
          :key="`${state.code}-label`"
          :x="state.labelX"
          :y="state.labelY"
          text-anchor="middle"
          dominant-baseline="middle"
          font-size="10"
          font-weight="bold"
          fill="#333"
          pointer-events="none"
          class="state-label"
        >
          {{ state.code }}
        </text>
      </g>

      <!-- Inset boxes for AK, HI, territories -->
      <g id="inset-states">
        <g
          v-for="state in insetStatesWithPath"
          :key="state.code"
        >
          <!-- Render actual path for AK and HI -->
          <path
            :d="state.path"
            :fill="getStateColor(state.code)"
            :stroke="getStateStroke(state.code)"
            stroke-width="1"
            class="state-path"
            @click.stop="handleStateClick(state.code)"
            @mouseenter="handleStateHover(state.code)"
          />
          <!-- Label for AK/HI -->
          <text
            :x="state.labelX"
            :y="state.labelY"
            text-anchor="middle"
            dominant-baseline="middle"
            font-size="10"
            font-weight="bold"
            fill="#333"
            pointer-events="none"
            class="state-label"
          >
            {{ state.code }}
          </text>
        </g>
        <!-- Box territories without paths -->
        <g
          v-for="state in visibleInsetStatesWithoutPath"
          :key="state.code"
          :transform="`translate(${state.insetX}, ${state.insetY})`"
        >
          <rect
            :width="insetBoxSize"
            :height="insetBoxSize"
            fill="#f3f4f6"
            stroke="#d1d5db"
            stroke-width="1"
          />
          <rect
            :width="insetBoxSize - 4"
            :height="insetBoxSize - 4"
            x="2"
            y="2"
            :fill="getStateColor(state.code)"
            :stroke="getStateStroke(state.code)"
            stroke-width="1"
            class="state-path"
            @click.stop="handleStateClick(state.code)"
            @mouseenter="handleStateHover(state.code)"
          />
          <text
            :x="insetBoxSize / 2"
            :y="insetBoxSize + 12"
            text-anchor="middle"
            font-size="10"
            font-weight="bold"
            fill="#333"
          >
            {{ state.code }}
          </text>
        </g>
      </g>
    </svg>

    <!-- Hover tooltip (non-insights mode) -->
    <div
      v-if="hoveredState && !useClickPopup"
      class="map-tooltip"
    >
      <div class="tooltip-header">
        <strong>{{ getStateName(hoveredState) }}</strong>
      </div>
      <div>{{ getStateMarginText(hoveredState) }}</div>
    </div>

    <!-- Click popup (insights mode) -->
    <div
      v-if="selectedState && useClickPopup"
      class="map-popup"
      @click.stop
    >
      <div class="popup-header">
        <div class="popup-title">
          <strong>{{ getStateName(selectedState) }}</strong>
          <template v-if="selectedDayLabel">
            <span class="tooltip-separator" />
            <span class="tooltip-day">{{ selectedDayLabel }}</span>
          </template>
        </div>
        <button
          type="button"
          class="popup-close"
          @click="closePopup"
        >
          &times;
        </button>
      </div>
      <div class="popup-body">
        <template v-if="stateScheduleDetails[selectedState]">
          <div
            v-for="(item, idx) in stateScheduleDetails[selectedState]"
            :key="idx"
            class="schedule-line"
          >
            <span class="schedule-hours">{{ item.hours }}</span>
            <span class="schedule-sources">
              <template v-for="(src, si) in item.sources">
                <strong
                  v-if="src.selected"
                  :key="si"
                >{{ src.name }}</strong>
                <span
                  v-else
                  :key="si"
                >{{ src.name }}</span>
                <span
                  v-if="si < item.sources.length - 1"
                  :key="'sep-' + si"
                >, </span>
              </template>
            </span>
          </div>
        </template>
        <template v-else>
          <div class="schedule-line">
            No demand
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import statesData from '@/data/us-states-svg.json'

export default {
  name: 'USStateMap',

  directives: {
    'click-outside': {
      bind(el, binding) {
        el._clickOutside = (e) => {
          if (!el.contains(e.target)) {
            binding.value()
          }
        }

        document.addEventListener('click', el._clickOutside)
      },
      unbind(el) {
        document.removeEventListener('click', el._clickOutside)
      }
    }
  },

  props: {
    stateOverrides: {
      type: Array,
      default: () => []
    },
    defaultMargins: {
      type: Object,
      default: () => ({ min: 0, max: 0 })
    },
    stateDemandCounts: {
      type: Object,
      default: null
    },
    showTerritories: {
      type: Boolean,
      default: true
    },
    stateScheduleDetails: {
      type: Object,
      default: null
    },
    selectedDayLabel: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      hoveredState: null,
      selectedState: null,
      insetBoxSize: 40
    }
  },

  computed: {
    mainStates() {
      return statesData.filter(s => s.position === 'main' && s.path)
    },

    insetStatesWithPath() {
      return statesData.filter(s => s.position === 'inset' && s.path)
    },

    insetStatesWithoutPath() {
      return statesData.filter(s => s.position === 'inset' && !s.path)
    },

    visibleInsetStatesWithoutPath() {
      if (!this.showTerritories) {
        return []
      }

      return this.insetStatesWithoutPath
    },

    useClickPopup() {
      return !!this.stateScheduleDetails
    },

    highlightedState() {
      return this.useClickPopup ? this.selectedState : this.hoveredState
    },

    maxDemandCount() {
      if (!this.stateDemandCounts) {
        return 0
      }

      const values = Object.values(this.stateDemandCounts)
      return values.length ? Math.max(...values) : 0
    }
  },

  watch: {
    selectedState(val) {
      if (val) {
        document.addEventListener('keydown', this._onKeyDown)
      } else {
        document.removeEventListener('keydown', this._onKeyDown)
      }
    }
  },

  created() {
    this._onKeyDown = (e) => {
      if (e.key === 'Escape') {
        this.closePopup()
      }
    }
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this._onKeyDown)
  },

  methods: {
    getStateColor(stateCode) {
      if (this.stateDemandCounts) {
        const count = this.stateDemandCounts[stateCode] || 0
        if (count === 0) {
          return '#e5e7eb'
        }

        if (count <= 2) {
          // Green: 1 = light, 2 = slightly darker
          const t = (count - 1)
          const r = Math.round(187 - t * 55)
          const g = Math.round(247 - t * 20)
          const b = Math.round(208 - t * 40)
          return `rgb(${r}, ${g}, ${b})`
        }

        if (count <= 5) {
          // Orange: 3 = light, 5 = slightly darker
          const t = (count - 3) / 2
          const r = Math.round(254 - t * 25)
          const g = Math.round(215 - t * 35)
          const b = Math.round(170 - t * 45)
          return `rgb(${r}, ${g}, ${b})`
        }

        // Red: 6+ = light, gradually slightly darker
        const maxRed = Math.max(this.maxDemandCount, 6)
        const t = maxRed > 6 ? (count - 6) / (maxRed - 6) : 0
        const r = Math.round(254 - t * 25)
        const g = Math.round(202 - t * 40)
        const b = Math.round(202 - t * 40)
        return `rgb(${r}, ${g}, ${b})`
      }

      if (!this.stateOverrides || !Array.isArray(this.stateOverrides)) {
        return '#e5e7eb'
      }

      const override = this.stateOverrides.find(s => s.stateCode === stateCode)
      if (!override) {
        return '#e5e7eb'
      } // Gray: no override

      const avgOverride = (override.minMarginPercent + override.maxMarginPercent) / 2
      const avgDefault = (this.defaultMargins.min + this.defaultMargins.max) / 2
      const diff = avgOverride - avgDefault

      if (diff < -5) {
        return '#4ade80'
      } // Green: lower margins

      if (diff > 5) {
        return '#f87171'
      } // Red: higher margins

      return '#fbbf24' // Yellow: similar margins
    },

    getStateStroke(stateCode) {
      return this.highlightedState === stateCode ? '#1f2937' : '#9ca3af'
    },

    handleStateClick(stateCode) {
      if (this.useClickPopup) {
        this.selectedState = this.selectedState === stateCode ? null : stateCode
      }

      this.$emit('state-click', stateCode)
    },

    handleStateHover(stateCode) {
      this.hoveredState = stateCode
    },

    onSvgMouseLeave() {
      this.hoveredState = null
    },

    closePopup() {
      this.selectedState = null
    },

    getStateName(stateCode) {
      const state = statesData.find(s => s.code === stateCode)
      return state ? state.name : stateCode
    },

    getStateMarginText(stateCode) {
      if (this.stateDemandCounts) {
        const count = this.stateDemandCounts[stateCode] || 0
        if (count === 0) {
          return 'No demand'
        }

        return count === 1 ? '1 insight item' : `${count} insight items`
      }

      if (this.stateOverrides && Array.isArray(this.stateOverrides)) {
        const override = this.stateOverrides.find(s => s.stateCode === stateCode)
        if (override) {
          return `Min: ${override.minMarginPercent}% | Max: ${override.maxMarginPercent}%`
        }
      }

      return `Default: Min ${this.defaultMargins.min}% | Max ${this.defaultMargins.max}%`
    }
  }
}
</script>

<style scoped>
.us-state-map {
  position: relative;
  width: 100%;
  max-width: 700px;
}

.state-path {
  cursor: pointer;
  transition: all 0.2s;
}

.state-path:hover {
  opacity: 0.8;
  filter: brightness(1.1);
}

.state-label {
  font-family: Arial, sans-serif;
  text-shadow: 0 0 2px #fff, 0 0 2px #fff, 0 0 2px #fff;
}

.map-tooltip {
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 14px;
  pointer-events: none;
  z-index: 1000;
}

.map-popup {
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.9);
  color: white;
  border-radius: 4px;
  font-size: 14px;
  z-index: 1000;
  max-height: 250px;
  display: flex;
  flex-direction: column;
}

.popup-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  flex-shrink: 0;
}

.popup-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.popup-close {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  padding: 0 0 0 12px;
}

.popup-close:hover {
  color: white;
}

.popup-body {
  padding: 4px 12px 8px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.tooltip-separator {
  width: 1px;
  height: 14px;
  background: rgba(255, 255, 255, 0.35);
}

.tooltip-day {
  color: #93c5fd;
  font-weight: 600;
}

.schedule-line {
  font-size: 12px;
  padding: 2px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  display: flex;
  gap: 8px;
}

.schedule-line:last-child {
  border-bottom: none;
}

.schedule-hours {
  color: #93c5fd;
  white-space: nowrap;
}

.schedule-sources {
  color: #d1d5db;
}
</style>
